import { TrackerTag } from '@/types/tracking';
import { useEffect, useRef } from 'react';
import { addtracking } from '../redux/features/tracking';
import { AttributionTrackingTags } from './constants';

/** Must match gatsby-plugin-chilli-ui and inferno-spark `clientSession.ts`. */
export const CLIENT_SESSION_STORAGE_KEY = 'client_session_id';

let trackingUserId: string | undefined;

function generateClientSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function readClientSessionCookie(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const cookiePrefix = `${CLIENT_SESSION_STORAGE_KEY}=`;
  const cookies = document.cookie.split(';');

  for (const cookiePart of cookies) {
    const trimmed = cookiePart.trim();
    if (trimmed.startsWith(cookiePrefix)) {
      const value = decodeURIComponent(trimmed.slice(cookiePrefix.length));
      if (value.length > 0) {
        return value;
      }
    }
  }

  return undefined;
}

function writeClientSessionCookie(sessionId: string): void {
  document.cookie = `${CLIENT_SESSION_STORAGE_KEY}=${encodeURIComponent(sessionId)};path=/;SameSite=Lax`;
}

export function getClientSessionId(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  const existing = readClientSessionCookie();
  if (existing) {
    return existing;
  }

  const sessionId = generateClientSessionId();
  writeClientSessionCookie(sessionId);
  return sessionId;
}

export function setTrackingUserId(userId: string | null | undefined): void {
  trackingUserId = userId && userId.length > 0 ? userId : undefined;
  if (typeof window !== 'undefined') {
    if (trackingUserId) {
      window.__chilliTrackingUserId = trackingUserId;
    } else {
      delete window.__chilliTrackingUserId;
    }
  }
}

export function clearTrackingUserId(): void {
  trackingUserId = undefined;
  if (typeof window !== 'undefined') {
    delete window.__chilliTrackingUserId;
  }
}

export function getTrackingUserId(): string | undefined {
  return trackingUserId;
}

export type asyncTrackingEventProps = {
  track?: string;
  trackAbacus?: string;
  trackId?: string;
  trackExternalId?: string;
  trackText?: any;
  trackUrl?: any;
  trackTarget?: string;
  trackValue?: any;
  trackMeta?: object;
};

/**
 * asycTrackingEvent
 * @param data tracking data
 */
export const asyncTrackingEvent = (data: asyncTrackingEventProps) => {
  const event = new CustomEvent('asyncTrackingEvent', { detail: data });
  // process.env.NODE_ENV == 'development' && console.debug('asyncTrackingEvent', event.detail);
  window.dispatchEvent(event);
};

/**
 * asyncVirtualPageViewEvent
 * @param data tracking data
 */
export const asyncVirtualPageViewEvent = (data: asyncTrackingEventProps) => {
  data.track = 'virtual_pageview';
  data.trackAbacus = 'virtual_pageview';
  const event = new CustomEvent('asyncTrackingEvent', { detail: data });
  // process.env.NODE_ENV == 'development' && console.debug('asyncVirtualPageViewEvent', event);
  window.dispatchEvent(event);
};

/**
 * aysncPageLoadEvent
 * Used when you want to fire an event off on page load.
 * @param location  react location object
 * @param data tracking data
 * @returns
 */
export const asyncPageLoadEvent = (location: any, data: asyncTrackingEventProps, changes: any = []) => {
  const locationRef = useRef({
    location: null
  });
  useEffect(() => {
    if (!locationRef.current.location) {
      locationRef.current.location = location;
      setTimeout(() => asyncTrackingEvent(data), 250);
    }
  }, changes);
  return locationRef;
};

export const initializeAttributionTracking = (location: any, dispatch: any) => {
  const params = new URLSearchParams(location.search);
  if (params.size > 0) {
    const tracking = AttributionTrackingTags.forEach((trackingTag: TrackerTag) => {
      trackingTag.tags.forEach((tag: string) => {
        const tagValue = params.get(tag);
        if (tagValue) {
          dispatch(
            addtracking({
              value: tagValue,
              source: location.pathname,
              ...trackingTag
            })
          );
        }
      });
    });
  }
};
